// ========== CUSTOM STUDY INLINE - GRAIA ==========
// Renderiza o conteúdo de estudo personalizado inline (sem modal/overlay)
// Usado em CycleDecksPage na aba "Estudo Personalizado"
// A lógica de negócio é idêntica ao CustomStudyModal

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Target,
    CheckSquare,
    Check,
    BookOpen,
    Layers,
    BarChart3,
    Play,
} from 'lucide-react';
import './CustomStudyInline.css';

interface StudyItem {
    _id: string;
    name: string;
    description?: string;
    cardCount?: number;
    subdeckCount?: number;
    isCustomDeck?: boolean;
}

interface Subdeck {
    _id: string;
    name: string;
    cardCount: number;
    groupId?: string | null;
}

interface DeckGroup {
    _id: string;
    name: string;
    color: string;
}

interface CustomStudyInlineProps {
    contextId: string;
    contextName: string;
    prefetchedItems?: any[] | null;
    prefetchedSubdecksMap?: Map<string, any[]> | null;
}

const CustomStudyInline: React.FC<CustomStudyInlineProps> = ({
    contextId,
    contextName,
    prefetchedItems,
    prefetchedSubdecksMap,
}) => {
    const navigate = useNavigate();

    const [items, setItems] = useState<StudyItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalCards, setTotalCards] = useState(0);

    const [decksSubdecksMap, setDecksSubdecksMap] = useState<Map<string, Subdeck[]>>(new Map());
    const [selectedSubdeckIds, setSelectedSubdeckIds] = useState<Set<string>>(new Set());
    const [activeDeckId, setActiveDeckId] = useState<string | null>(null);

    const [customDeckIds, setCustomDeckIds] = useState<Set<string>>(new Set());

    const [deckGroupsMap, setDeckGroupsMap] = useState<Map<string, DeckGroup[]>>(new Map());
    const deckGroupsCacheRef = useRef<Set<string>>(new Set()); // tracks which deckIds already fetched/fetching

    // ========== CARREGAR DADOS ==========
    useEffect(() => {
        if (prefetchedItems && prefetchedItems.length > 0) {
            const filtered = prefetchedItems.filter((d: any) => !d.isBlocked);
            setItems(filtered);

            // ← NOVO: reconstruir customDeckIds a partir dos prefetchedItems
            // O prefetch em CycleDecksPage já marca isCustomDeck: true/false
            const customIds = new Set<string>(
                filtered
                    .filter((d: any) => d.isCustomDeck === true)
                    .map((d: any) => d._id)
            );
            setCustomDeckIds(customIds);

            if (prefetchedSubdecksMap) {
                setDecksSubdecksMap(prefetchedSubdecksMap);
            }
            setLoading(false);
            return;
        }
        loadItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextId]);

    useEffect(() => {
        calculateTotalCards();
        // eslint-disable-next-line
    }, [selectedSubdeckIds, decksSubdecksMap]);

    const loadItems = async () => {
        if (prefetchedItems && prefetchedItems.length > 0) {
            const filtered = prefetchedItems.filter((d: any) => !d.isBlocked);
            setItems(filtered);

            const customIds = new Set<string>(
                filtered
                    .filter((d: any) => d.isCustomDeck === true)
                    .map((d: any) => d._id)
            );
            setCustomDeckIds(customIds);

            if (prefetchedSubdecksMap) setDecksSubdecksMap(prefetchedSubdecksMap);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');

            const [officialRes, customRes] = await Promise.all([
                fetch(`/api/cycles/${contextId}/decks`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                fetch(`/api/custom-decks/public/${contextId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            let allDecks: StudyItem[] = [];

            if (officialRes.ok) {
                const data = await officialRes.json();
                const official = (data.decks || data)
                    .filter((deck: any) => deck.purchased === true && !deck.isBlocked)
                    .map((d: any) => ({
                        _id: d._id,
                        name: d.name,
                        description: d.description || '',
                        subdeckCount: d.subdeckCount || 0,
                        isCustomDeck: false, // ← NOVO: decks oficiais não são custom
                    }));
                allDecks = [...allDecks, ...official];
            }

            if (customRes.ok) {
                const customData = await customRes.json();
                const custom = customData
                    .filter((d: any) => (d.purchased === true || d.isFree === true) && !d.isBlocked)
                    .map((d: any) => ({
                        _id: d._id,
                        name: d.name,
                        description: d.description || '',
                        subdeckCount: d.subdeckCount || 0,
                        isCustomDeck: true, // ← NOVO: marcar como custom deck
                    }));
                allDecks = [...allDecks, ...custom];

                // ← NOVO: guardar IDs dos custom decks
                setCustomDeckIds(new Set(custom.map((d: any) => d._id)));
            }

            setItems(allDecks);
            await loadAllSubdecks(allDecks);
        } catch (error) {
            console.error('Erro ao carregar itens:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAllSubdecks = async (decks: StudyItem[]) => {
        const token = localStorage.getItem('accessToken');
        const newMap = new Map<string, Subdeck[]>();

        for (const deck of decks) {
            try {
                let response: Response;

                // ← NOVO: usar endpoint correto baseado no tipo do deck
                if (deck.isCustomDeck) {
                    response = await fetch(`/api/custom-decks/${deck._id}/subdecks-meta`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } else {
                    response = await fetch(`/api/decks/${deck._id}/subdecks-meta`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    // Fallback para custom-decks se o endpoint normal falhar
                    if (!response.ok) {
                        response = await fetch(`/api/custom-decks/${deck._id}/subdecks-meta`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    }
                }

                if (response.ok) {
                    const data = await response.json();
                    const subdecks = (data.subdecks || data)
                        .filter((s: any) => !s.isBlocked && s.type !== 'apostila')
                        .map((s: any) => ({
                            _id: s._id,
                            name: s.name,
                            cardCount: s.cardCount || 0,
                            groupId: s.groupId || null,
                        }));
                    newMap.set(deck._id, subdecks);
                }
            } catch (error) {
                console.error(`Erro ao carregar subdecks do deck ${deck._id}:`, error);
            }
        }

        setDecksSubdecksMap(newMap);
    };

    const loadDeckGroups = async (deckId: string) => {
        if (deckGroupsCacheRef.current.has(deckId)) return;
        deckGroupsCacheRef.current.add(deckId);
        try {
            if (customDeckIds.has(deckId)) {
                setDeckGroupsMap(prev => new Map(prev).set(deckId, []));
                return;
            }
            const token = localStorage.getItem('accessToken');
            const r = await fetch(`/api/decks/${deckId}/groups`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (r.ok) {
                const data = await r.json();
                const groups: DeckGroup[] = (data as any[])
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map(g => ({ _id: g._id, name: g.name, color: g.color || '#9CA3AF' }));
                setDeckGroupsMap(prev => new Map(prev).set(deckId, groups));
            } else {
                setDeckGroupsMap(prev => new Map(prev).set(deckId, []));
            }
        } catch {
            setDeckGroupsMap(prev => new Map(prev).set(deckId, []));
        }
    };

    const calculateTotalCards = () => {
        let total = 0;
        selectedSubdeckIds.forEach(subdeckId => {
            decksSubdecksMap.forEach(subdecks => {
                const subdeck = subdecks.find(s => s._id === subdeckId);
                if (subdeck) total += subdeck.cardCount;
            });
        });
        setTotalCards(total);
    };

    // ========== HANDLERS ==========
    const handleToggleSubdeck = (subdeckId: string) => {
        setSelectedSubdeckIds(prev => {
            const next = new Set(prev);
            next.has(subdeckId) ? next.delete(subdeckId) : next.add(subdeckId);
            return next;
        });
    };

    const handleSelectAllSubdecksOfDeck = (deckId: string) => {
        const subdecks = decksSubdecksMap.get(deckId) || [];
        const ids = subdecks.map(s => s._id);
        const allSelected = ids.every(id => selectedSubdeckIds.has(id));

        setSelectedSubdeckIds(prev => {
            const next = new Set(prev);
            allSelected
                ? ids.forEach(id => next.delete(id))
                : ids.forEach(id => next.add(id));
            return next;
        });
    };

    const handleSelectAll = () => {
        const allIds = new Set<string>();
        decksSubdecksMap.forEach(subdecks => subdecks.forEach(s => allIds.add(s._id)));

        if (selectedSubdeckIds.size === allIds.size) {
            setSelectedSubdeckIds(new Set());
        } else {
            setSelectedSubdeckIds(allIds);
        }
    };

    const handleStartStudy = () => {
        if (selectedSubdeckIds.size === 0) return;

        const studyPool: any[] = [];
        decksSubdecksMap.forEach((subdecks, deckId) => {
            subdecks.forEach(subdeck => {
                if (selectedSubdeckIds.has(subdeck._id)) {
                    studyPool.push({
                        cycleId: contextId,
                        deckId,
                        subdeckId: subdeck._id,
                        isCustomDeck: customDeckIds.has(deckId), // ← NOVO: crucial para o hook usar endpoint correto
                    });
                }
            });
        });

        localStorage.setItem('customStudyPool', JSON.stringify(studyPool));
        localStorage.setItem('customStudyTotalCards', totalCards.toString());
        sessionStorage.setItem('graia_theme_context', 'enem');
        navigate('/study/custom');
    };

    const getTotalSelected = () => selectedSubdeckIds.size;

    const getTotalDecksSelected = () => {
        const decksWithSelection = new Set<string>();
        selectedSubdeckIds.forEach(subdeckId => {
            decksSubdecksMap.forEach((subdecks, deckId) => {
                if (subdecks.some(s => s._id === subdeckId)) decksWithSelection.add(deckId);
            });
        });
        return decksWithSelection.size;
    };

    const hasSelection = getTotalSelected() > 0;

    // ========== RENDER ==========
    if (loading) {
        return (
            <div className="csi-loading">
                <div className="csi-spinner" />
                <p>Carregando conteúdos...</p>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="csi-empty">
                <BookOpen size={52} strokeWidth={1.4} />
                <h3>Nenhum conteúdo disponível</h3>
                <p>Você ainda não possui decks adquiridos neste ciclo.</p>
            </div>
        );
    }

    const activeDeck = items.find(d => d._id === activeDeckId);
    const activeSubdecks = activeDeckId ? decksSubdecksMap.get(activeDeckId) || [] : [];
    const activeSelectedCount = activeSubdecks.filter(s => selectedSubdeckIds.has(s._id)).length;
    const activeGroups = activeDeckId ? deckGroupsMap.get(activeDeckId) : undefined;

    return (
        <div className="csi-root">

            {/* ── HEADER DA SEÇÃO ── */}
            <div className="csi-section-header">
                <div className="csi-section-title">
                    <Target size={18} />
                    <span>Estudo Personalizado</span>
                </div>
                <div className="csi-section-actions">
                    <button className="csi-btn-select-all" onClick={handleSelectAll}>
                        <CheckSquare size={15} />
                        {selectedSubdeckIds.size > 0 ? 'Desmarcar todos' : 'Selecionar todos'}
                    </button>
                </div>
            </div>

            {/* ── SPLIT VIEW ── */}
            <div className="csi-split">

                {/* LEFT: deck list */}
                <div className="csi-sidebar">
                    {items.map(deck => {
                        const isActive = activeDeckId === deck._id;
                        const subdecks = decksSubdecksMap.get(deck._id) || [];
                        const selected = subdecks.filter(s => selectedSubdeckIds.has(s._id)).length;

                        return (
                            <button
                                key={deck._id}
                                className={`csi-deck-item ${isActive ? 'active' : ''}`}
                                onClick={() => { setActiveDeckId(deck._id); loadDeckGroups(deck._id); }}
                            >
                                <div className="csi-deck-icon">
                                    <Layers size={18} />
                                </div>
                                <div className="csi-deck-info">
                                    <span className="csi-deck-name">{deck.name}</span>
                                    <span className="csi-deck-count">
                                        {selected}/{deck.subdeckCount} selecionados
                                    </span>
                                </div>
                                {selected > 0 && (
                                    <div className="csi-deck-badge">{selected}</div>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* RIGHT: subdecks grid */}
                <div className="csi-panel">
                    {activeDeck ? (
                        <>
                            <div className="csi-panel-header">
                                <h3 className="csi-panel-title">{activeDeck.name}</h3>
                                <div className="csi-panel-controls">
                                    <button
                                        className="csi-btn-select-deck"
                                        onClick={() => handleSelectAllSubdecksOfDeck(activeDeck._id)}
                                    >
                                        <CheckSquare size={14} />
                                        {activeSelectedCount === activeSubdecks.length
                                            ? 'Desmarcar todos'
                                            : 'Selecionar todos'}
                                    </button>
                                    <span className="csi-counter">
                                        {activeSelectedCount}/{activeSubdecks.length}
                                    </span>
                                </div>
                            </div>

                            {(() => {
                                const renderCard = (subdeck: Subdeck) => {
                                    const isSelected = selectedSubdeckIds.has(subdeck._id);
                                    return (
                                        <div
                                            key={subdeck._id}
                                            className={`csi-subdeck-card ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleToggleSubdeck(subdeck._id)}
                                        >
                                            <div className="csi-subdeck-name">{subdeck.name}</div>
                                            <div className="csi-subdeck-footer">
                                                <span className="csi-subdeck-cards">{subdeck.cardCount} cards</span>
                                                <div className={`csi-checkbox ${isSelected ? 'checked' : ''}`}>
                                                    {isSelected && <Check size={12} />}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                };

                                if (!activeGroups || activeGroups.length === 0) {
                                    return (
                                        <div className="csi-subdecks-grid">
                                            {activeSubdecks.map(s => renderCard(s))}
                                        </div>
                                    );
                                }

                                const byGroup = new Map<string | null, Subdeck[]>();
                                activeSubdecks.forEach(s => {
                                    const gId = s.groupId || null;
                                    if (!byGroup.has(gId)) byGroup.set(gId, []);
                                    byGroup.get(gId)!.push(s);
                                });

                                return (
                                    <div className="csi-subdecks-scroll">
                                        {activeGroups.map(group => {
                                            const groupSubdecks = byGroup.get(group._id) || [];
                                            if (groupSubdecks.length === 0) return null;
                                            return (
                                                <div key={group._id} className="csi-group-section">
                                                    <div className="csi-group-header">
                                                        <span className="csi-group-dot" style={{ background: group.color }} />
                                                        <span>{group.name}</span>
                                                    </div>
                                                    <div className="csi-subdecks-grid csi-subdecks-grid--inline">
                                                        {groupSubdecks.map(s => renderCard(s))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {(byGroup.get(null) || []).length > 0 && (
                                            <div className="csi-group-section">
                                                <div className="csi-group-header">
                                                    <span className="csi-group-dot" style={{ background: '#9CA3AF' }} />
                                                    <span>Sem grupo</span>
                                                </div>
                                                <div className="csi-subdecks-grid csi-subdecks-grid--inline">
                                                    {(byGroup.get(null) || []).map(s => renderCard(s))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        <div className="csi-panel-empty">
                            <Layers size={48} strokeWidth={1.2} />
                            <p>Selecione um deck ao lado para ver os subdecks</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ── POWER BAR (fixed bottom, aparece quando há seleção) ── */}
            <div className={`csi-powerbar ${hasSelection ? 'visible' : ''}`}>
                <div className="csi-powerbar-stats">
                    <span className="csi-powerbar-stat">
                        <Layers size={16} />
                        <strong>{getTotalDecksSelected()}</strong> decks
                    </span>
                    <div className="csi-powerbar-divider" />
                    <span className="csi-powerbar-stat">
                        <BookOpen size={16} />
                        <strong>{getTotalSelected()}</strong> subdecks
                    </span>
                    <div className="csi-powerbar-divider" />
                    <span className="csi-powerbar-stat">
                        <BarChart3 size={16} />
                        <strong>{totalCards}</strong> cards
                    </span>
                </div>
                <button
                    className="csi-btn-start"
                    onClick={handleStartStudy}
                    disabled={!hasSelection}
                >
                    <Play size={15} />
                    Iniciar Estudo
                </button>
            </div>

        </div>
    );
};

export default CustomStudyInline;